/**
 * Tracks whether the Usercentrics CMP UI is open so app focus traps can yield.
 * Usercentrics is typically injected via GTM (not in this repo).
 */

const UC_OPEN_EVENT_TYPES = new Set(['CMP_SHOWN'])

const UC_CLOSE_EVENT_TYPES = new Set([
  'ACCEPT_ALL',
  'DENY_ALL',
  'SAVE',
  'SAVE_CC'
])

const UC_ROOT_SELECTORS = [
  '#usercentrics-root',
  '#uc-main-dialog',
  '[data-testid="uc-app-container"]'
].join(',')

const OBSERVER_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['style', 'class', 'hidden', 'aria-hidden']
}

type UcUiApi = {
  isConsentRequired?: () => boolean
}

declare global {
  interface Window {
    UC_UI?: UcUiApi
  }
}

let listenersAttached = false

const isElementVisible = (el: HTMLElement): boolean => {
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
    return false
  }
  return el.getClientRects().length > 0
}

/** Walks up through light DOM and shadow hosts. */
const isInsideUsercentrics = (el: Element | null): boolean => {
  let current: Element | null = el
  while (current) {
    if (
      current.id === 'usercentrics-root'
      || current.id === 'uc-main-dialog'
      || (typeof current.id === 'string' && current.id.startsWith('usercentrics'))
      || current.getAttribute?.('data-testid') === 'uc-app-container'
    ) {
      return true
    }
    const root = current.getRootNode()
    if (root instanceof ShadowRoot) {
      current = root.host
      continue
    }
    current = current.parentElement
  }
  return false
}

const getUcRoots = (): HTMLElement[] => {
  return Array.from(document.querySelectorAll(UC_ROOT_SELECTORS)).filter(
    (el): el is HTMLElement => el instanceof HTMLElement
  )
}

export const isUsercentricsDomVisible = (): boolean => {
  if (!import.meta.client) {
    return false
  }

  for (const root of getUcRoots()) {
    if (isElementVisible(root)) {
      return true
    }
  }

  return isInsideUsercentrics(document.activeElement)
}

const isConsentRequired = (): boolean => {
  try {
    return Boolean(window.UC_UI?.isConsentRequired?.())
  }
  catch {
    // UC_UI may throw before fully ready
    return false
  }
}

const syncFromUcApi = (isOpen: Ref<boolean>): void => {
  if (isConsentRequired()) {
    isOpen.value = true
  }

  if (isUsercentricsDomVisible()) {
    isOpen.value = true
  }
}

const attachListeners = (isOpen: Ref<boolean>): void => {
  if (!import.meta.client || listenersAttached) {
    return
  }
  listenersAttached = true

  let observer: MutationObserver | null = null
  let observingDocument = false
  let observerStopped = false

  const disconnectObserver = (): void => {
    if (!observer) {
      return
    }
    observer.disconnect()
    observer = null
    observingDocument = false
    observerStopped = true
  }

  const observeDocument = (): void => {
    if (observerStopped || !observer) {
      return
    }
    observer.disconnect()
    observer.observe(document.documentElement, OBSERVER_OPTIONS)
    observingDocument = true
  }

  const observeRoots = (roots: HTMLElement[]): void => {
    if (observerStopped || !observer || roots.length === 0) {
      return
    }
    observer.disconnect()
    for (const root of roots) {
      observer.observe(root, OBSERVER_OPTIONS)
    }
    observingDocument = false
  }

  const stopObserverIfConsentDone = (): boolean => {
    if (observerStopped) {
      return true
    }
    // Returning visitor / consent already stored — banner will not auto-show again
    if (window.UC_UI && !isConsentRequired()) {
      disconnectObserver()
      return true
    }
    return false
  }

  const retargetObserver = (): void => {
    if (observerStopped || !observer) {
      return
    }
    if (stopObserverIfConsentDone()) {
      return
    }

    const roots = getUcRoots()
    if (roots.length > 0) {
      if (observingDocument) {
        observeRoots(roots)
      }
      return
    }

    if (!observingDocument) {
      observeDocument()
    }
  }

  let domSyncScheduled = false
  const scheduleDomSync = (): void => {
    if (observerStopped || domSyncScheduled) {
      return
    }
    domSyncScheduled = true
    requestAnimationFrame(() => {
      domSyncScheduled = false
      if (observerStopped) {
        return
      }

      const visible = isUsercentricsDomVisible()
      if (visible) {
        isOpen.value = true
      }
      else if (getUcRoots().length > 0) {
        // Root exists but is hidden → CMP dismissed / not shown
        isOpen.value = false
      }

      retargetObserver()
    })
  }

  window.addEventListener('UC_UI_CMP_EVENT', ((event: CustomEvent<{ type?: string }>) => {
    const type = event.detail?.type
    if (!type) {
      return
    }
    if (UC_OPEN_EVENT_TYPES.has(type)) {
      isOpen.value = true
    }
    else if (UC_CLOSE_EVENT_TYPES.has(type)) {
      isOpen.value = false
      // Consent confirmed — banner will not auto-show again
      disconnectObserver()
    }
  }) as EventListener)

  window.addEventListener('UC_UI_INITIALIZED', () => {
    syncFromUcApi(isOpen)
    stopObserverIfConsentDone()
  })

  // UC may already be ready when this composable first runs
  if (window.UC_UI) {
    syncFromUcApi(isOpen)
  }

  if (stopObserverIfConsentDone()) {
    return
  }

  observer = new MutationObserver(scheduleDomSync)
  const roots = getUcRoots()
  if (roots.length > 0) {
    observeRoots(roots)
  }
  else {
    observeDocument()
  }
}

/**
 * Shared flag: Usercentrics first/second layer (or similar) is currently shown.
 * Also exposes a live check that includes DOM/focus fallbacks.
 */
export const useUsercentricsOverlay = () => {
  const isUsercentricsOverlayOpen = useState('usercentrics-overlay-open', () => false)

  attachListeners(isUsercentricsOverlayOpen)

  const isBlockingFocusTraps = (): boolean => {
    return isUsercentricsOverlayOpen.value || isUsercentricsDomVisible()
  }

  return {
    isUsercentricsOverlayOpen,
    isBlockingFocusTraps,
    isUsercentricsDomVisible
  }
}

export default useUsercentricsOverlay
